import { useState } from 'react'
import type { EiaClient } from '../../api/client'
import type { Artefact } from '../../api/types'

interface ArtefactCardProps {
  client: EiaClient
  engagementId: string
  artefacts: readonly Artefact[]
  specSha256: string | null
  onOpenReport: () => void
}

/**
 * The artefact card. `04 §6`.
 *
 * <p><b>THE REPORT IS NEVER OFFERED ALONE.</b> "A report without its specification cannot be traced
 * back to origins, which is the property the whole design exists to provide." So the card offers
 * the report, `report.md`, the frozen specification and the audit trail TOGETHER, with the
 * specification's `sha256` beside them.</p>
 *
 * <p>Downloads go through the BFF, which brokers a short-lived SAS scoped to one blob after the
 * ownership check. <b>They are BUTTONS rather than links</b>: the brokering route is
 * token-protected and a link navigation cannot carry a token, so an `<a href>` to it was answered
 * 401 every time (B-162). The click asks for the URL with the token and then goes to it, which
 * keeps the storage URL off the page until the moment it is used and out of the browser's history
 * of visited links entirely.</p>
 */
export function ArtefactCard(
  { client, engagementId, artefacts, specSha256, onOpenReport }: ArtefactCardProps,
): React.JSX.Element | null {
  const [failed, setFailed] = useState<string | null>(null)

  if (artefacts.length === 0) return null

  const report = artefacts.find((a) => a.name === 'report.html')

  const download = (name: string): void => {
    void client.artefactLink(engagementId, name)
      .then((url) => {
        setFailed(null)
        window.location.assign(url)
      })
      .catch(() => setFailed(name))
  }

  return (
    <section className="artefacts" id="artefacts" aria-label="Artefacts">
      <p className="mlabel">Signed and released</p>
      <h3 style={{ fontSize: '19px', margin: '6px 0 8px' }}>The report and its specification</h3>
      <p className="note" style={{ fontSize: '14px', maxWidth: '60ch' }}>
        A report without its specification cannot be traced back to origins, which is the property
        this whole design exists to provide. Both are offered together, always.
      </p>

      <div className="row">
        {report && (
          <button type="button" className="btn primary" onClick={onOpenReport}>Open report</button>
        )}
        {artefacts.filter((a) => a.name !== 'report.html').map((artefact) => (
          <button type="button" className="btn quiet" key={artefact.name}
                  onClick={() => download(artefact.name)}>
            ⬇︎ {artefact.name}
          </button>
        ))}
      </div>

      {failed !== null && (
        <p className="note" role="alert">
          {failed} could not be prepared for download. It is still stored; try again.
        </p>
      )}

      {/* EVERY ARTEFACT'S DIGEST, not only the specification's. `AC-UI-9` and `AC-ART-4` ask that
          the DISPLAYED sha256 match the file, and a card that shows one hash beside four downloads
          lets a reader check one of them. */}
      <dl className="sha-list" aria-label="SHA-256 digests">
        {artefacts.filter((a) => a.sha256 !== null && a.sha256 !== undefined).map((artefact) => (
          <div key={artefact.name}>
            <dt>{artefact.name}</dt>
            <dd className="sha">{artefact.sha256}</dd>
          </div>
        ))}

        {/* THE SPECIFICATION'S OWN DIGEST, IN THE SAME GRID (B-225). As a separate paragraph it
            carried its label inline and lined up with nothing above it. */}
        {specSha256 && (
          <div>
            <dt>specification sha256</dt>
            <dd className="sha">{specSha256}</dd>
          </div>
        )}
      </dl>
    </section>
  )
}
